import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "@phosphor-icons/react";
import { useSeo } from "../lib/seo";
import { PRESS_QUOTES } from "../lib/data";

export default function Press() {
  useSeo({
    title: "Shaji Nada | Press — Coverage & Media Inquiries",
    description:
      "Selected press coverage of Shaji Nada and his ventures Maajja and Ideal Entertainment — Rolling Stone India, The Week, CBC, American Bazaar, and more.",
    canonicalPath: "/press",
  });

  return (
    <div data-testid="press-page" className="pt-32 md:pt-40 pb-24">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12">
        <p className="overline mb-6">Press</p>
        <h1 className="font-serif-display text-5xl md:text-7xl lg:text-8xl text-white leading-[0.98] max-w-5xl" data-testid="press-h1">
          What journalists are <span className="italic text-[var(--accent)]">saying.</span>
        </h1>
      </section>

      <section className="mt-20 md:mt-28 max-w-[1400px] mx-auto px-6 md:px-12 space-y-20 md:space-y-28">
        {PRESS_QUOTES.map((q, idx) => (
          <motion.figure
            key={`${q.outlet}-${idx}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.05 * idx }}
            className={`grid md:grid-cols-12 gap-8 items-start ${idx % 2 === 1 ? "md:pl-24" : ""}`}
            data-testid={`press-quote-${idx}`}
          >
            <div className="md:col-span-2">
              <span className="font-serif-display text-[var(--accent)] italic text-6xl leading-none">"</span>
            </div>
            <blockquote className="md:col-span-10 font-serif-display text-3xl md:text-5xl text-white leading-[1.08]">
              {q.quote}
              <figcaption className="mt-8 flex items-center gap-3 text-sm uppercase tracking-[0.22em] text-white/60">
                <span className="w-12 h-px bg-[var(--accent)]" />
                <a href={q.href} target="_blank" rel="noopener noreferrer" className="hover:text-[var(--accent)] transition-colors inline-flex items-center gap-2">
                  {q.outlet}
                  <ArrowUpRight size={14} />
                </a>
              </figcaption>
            </blockquote>
          </motion.figure>
        ))}
      </section>

      {/* Media inquiries CTA */}
      <section className="mt-32 md:mt-48 max-w-[1400px] mx-auto px-6 md:px-12 border-t border-white/10 pt-20" data-testid="press-media-cta">
        <div className="grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-8">
            <p className="overline mb-6">Media Inquiries</p>
            <h2 className="font-serif-display text-4xl md:text-6xl text-white leading-[1.02]">
              Working on a story? <br />
              <span className="italic text-[var(--accent)]">Reach out directly.</span>
            </h2>
            <p className="mt-6 text-white/60 max-w-xl font-light">
              For interview requests, embargoed previews, and biography assets — please contact the press desk.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right space-y-4">
            <a href="mailto:Shaji@idealgroup.ca" className="block link-underline text-white text-lg" data-testid="press-email">
              Shaji@idealgroup.ca
            </a>
            <a href="tel:+14163566500" className="block link-underline text-white/70 text-base" data-testid="press-phone">
              +1 (416) 356-6500
            </a>
            <Link
              to="/contact"
              data-testid="press-contact-cta"
              className="inline-flex items-center gap-3 px-7 py-4 border border-[var(--accent)] text-[var(--accent)] uppercase tracking-[0.22em] text-xs font-medium hover:bg-[var(--accent)] hover:text-black transition-colors duration-300"
            >
              Press inquiries
              <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
